import { i18nFunct } from '@lucid/gatsby-theme-lucid';
import { graphql, useStaticQuery } from 'gatsby';
import throttle from 'lodash.throttle';
import { Box, Heading } from 'lucid-components-react';
import React, { useContext, useEffect, useState } from 'react';

import LayoutContext from 'contexts/layoutContext';
import {
  Drupal_ParagraphFeatureGridItem,
  Drupal_ParagraphFeatureGridSection
} from 'generated/graphql';
import theme from 'theme/theme';
import { isDefined } from 'types/types';
import FeatureGridItem from './feature-grid-item/feature-grid-item';

interface Props {
  data: Drupal_ParagraphFeatureGridSection;
}

const FeatureGrid = ({ data }: Props) => {
  const DEFAULT_ROWS = 4;
  const { fieldItemsPerRow, fieldSectionTitle, fieldFeatureGridItem } = data;
  const featureGridItems = fieldFeatureGridItem
    ? fieldFeatureGridItem
      .filter(isDefined)
      .map(item => item.entity)
      .filter(isDefined)
      .map(entity => entity as Drupal_ParagraphFeatureGridItem)
    : [];

  const calculateItemsPerRow = () => {
    if (typeof window !== 'undefined') {
      const windowWidth = window.innerWidth;
      // TODO we need a better way of structuring breakpoints
      // so we dont have to cast as a string here.
      if (windowWidth > parseInt(theme.breakpoints[2])) {
        return fieldItemsPerRow || DEFAULT_ROWS;
      }
      if (windowWidth > parseInt(theme.breakpoints[1])) {
        return 2;
      }
      return 1;
    }
    return 4;
  };

  const [calculatedItemsPerRow, setCalculatedItemsPerRow] = useState(
    calculateItemsPerRow()
  );

  const updateItemsPerRow = (num: number) => {
    if (calculatedItemsPerRow !== num) {
      setCalculatedItemsPerRow(num);
    }
  };

  const { i18nData } = useStaticQuery(
    graphql`
      {
        i18nData: allFile(filter: { sourceInstanceName: { eq: "i18n" } }) {
          edges {
            node {
              name
              childSrcJson {
                mktg_abtest_gdpr_compliant_link
              }
            }
          }
        }
      }
    `
  );

  const handleResize = throttle(
    () => {
      const itemsPerRow = calculateItemsPerRow();
      updateItemsPerRow(itemsPerRow);
    },
    100,
    { trailing: true }
  );

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    // start things off with a resize to get initial values
    handleResize();
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  });

  const {
    language: { i18nKey }
  } = useContext(LayoutContext);

  const rows = Math.ceil(featureGridItems.length / calculatedItemsPerRow);
  const max = rows * calculatedItemsPerRow;
  const i18n = i18nFunct(i18nData, i18nKey);

  return (
    <Box css={theme => ({ backgroundColor: theme.colors.coolGrey[5] })}>
      <Box
        m="0 auto"
        maxWidth="1018px"
        p={{ default: '1.2rem', tabletSmall: '4rem 2rem' }}
      >
        {fieldSectionTitle && (
          <Heading variant="subheadS">{fieldSectionTitle}</Heading>
        )}
        <Box display="flex" flexDirection="row" flexWrap="wrap">
          {featureGridItems.map((itemEntity, i) => {
            const placeFromLast = Math.abs(i - max);
            const hasBorder = placeFromLast > calculatedItemsPerRow;
            if (!itemEntity.fieldFeatureDescription) {
              return null;
            }
            return (
              <Box
                key={itemEntity.id || `feature-grid-item-${i}`}
                as="article"
                css={{ flex: `0 1 ${100 / calculatedItemsPerRow}%` }}
              >
                <FeatureGridItem
                  buttonText={i18n('mktg-abtest-gdpr-compliant-link')}
                  hasBorder={hasBorder}
                  item={itemEntity}
                />
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default FeatureGrid;
